import { useState } from 'react';
import { HelpTooltip } from '../../../../shared/components/HelpTooltip';
import type { EmployeeProfileStepProps } from './EmployeeSetupStep';
import { FederalTaxElectionSection } from './FederalTaxElectionSection';
import { AdminTaxSettingsPanel } from './AdminTaxSettingsPanel';
import { ReciprocityReviewSection } from './ReciprocityReviewSection';
import { StateIncomeTaxSection } from './StateIncomeTaxSection';
import { StateElectionsSection } from './StateElectionsSection';
import { useAdminStateTax, type AdminStateTaxState } from './useAdminStateTax';
import { useAdminTaxSetup } from './useAdminTaxSetup';

const SUBSECTIONS = [
  { key: 'federal', label: 'Federal Income Tax' },
  { key: 'state', label: 'State Income Tax' },
  { key: 'settings', label: 'Tax Settings' },
  { key: 'jurisdiction', label: 'Tax Jurisdiction Summary' },
] as const;

type SubsectionKey = (typeof SUBSECTIONS)[number]['key'];

const NOT_CONNECTED = 'Comes from the tax engine integration (EPN-22), which is not connected yet. Nothing is calculated, inferred or saved here.';
const LOCAL_NOT_AVAILABLE =
  'Local tax jurisdictions are not determined yet: that needs a verified classification of the tax engine\'s county and city items, which is not connected. Nothing is inferred or saved here.';

/** A jurisdiction fact. With a `value` it is real data EPN already holds (read-only); without one it says "Not available yet" and why. */
function JurisdictionFact({ label, value, reason = NOT_CONNECTED }: { label: string; value?: string | null; reason?: string }) {
  return (
    <div className="epx-fact">
      <span className="epx-fact__label">{label} <HelpTooltip label={`About ${label}`} text={reason} /></span>
      <span className={value ? 'epx-fact__value' : 'epx-fact__value epx-fact__value--empty'}>{value ?? 'Not available yet'}</span>
    </div>
  );
}

/** The facts the state-tax read provides, or the honest reason there is no value yet (still loading, failed, or the state is unknown). */
function stateFact(stateTax: AdminStateTaxState, pick: 'workState' | 'residenceState'): { value: string | null; reason: string } {
  if (stateTax.failed) return { value: null, reason: 'The state income tax details could not be loaded. Open State Income Tax to retry.' };
  if (stateTax.data === null) return { value: null, reason: 'Loading.' };
  const state = stateTax.data[pick];
  return { value: state.name ?? state.code, reason: state.note };
}

/** Tax Jurisdiction Summary, read-only: only what EPN already holds. Local jurisdictions stay "Not available yet" with the reason. */
function JurisdictionSummary({ stateTax }: { stateTax: AdminStateTaxState }) {
  const work = stateFact(stateTax, 'workState');
  const residence = stateFact(stateTax, 'residenceState');
  const data = stateTax.data;
  return (
    <section className="profile-section">
      <h3 className="profile-section-title">Tax Jurisdiction Summary <span className="profile-section-badge">Read-only</span></h3>
      <div className="epx-facts">
        <JurisdictionFact label="Tax Work State" value={work.value} reason={work.reason} />
        <JurisdictionFact label="Tax Residence State" value={residence.value} reason={residence.reason} />
        <JurisdictionFact
          label="Multi-State Status"
          value={data ? { NotApplicable: 'Not applicable', ReviewRequired: 'Review required', Unresolved: 'Unresolved' }[data.multiState.status] : null}
          reason={data ? data.multiState.message : 'Loading, or not loaded. Open State Income Tax to retry.'}
        />
        <JurisdictionFact label="Reciprocity" value={data ? data.reciprocity.label : null} reason="Reviewed under Tax Settings. A certificate is a request, not an exemption." />
        <JurisdictionFact label="Local Tax Jurisdiction Work" reason={LOCAL_NOT_AVAILABLE} />
        <JurisdictionFact label="Local Tax Jurisdiction Residence" reason={LOCAL_NOT_AVAILABLE} />
      </div>
    </section>
  );
}

// EPN-22 owns the tax engine/integration boundary (calculations, local-tax inference, SUI/SDI/PFL, and the actual withholding/exemption persistence).
// Federal Income Tax and the reciprocity review are real. State withholding elections are entered and reviewed here (EPN-52 PR 4). State Income Tax is
// real and READ-ONLY (EPN-245): jurisdiction facts and the employee's saved
// answers from EPN's own records, plus the tax engine sandbox's form-field metadata. Tax Settings is the guided, read-only tax setup (status, facts with
// their sources, the seven-row checklist) plus the reciprocity review; its status is informational and changes no payroll behaviour. No calculation, no
// fake persistence, no invented tax contract.
export function TaxesExemptionsStep({ employee, statusPill, footer }: EmployeeProfileStepProps) {
  const [sub, setSub] = useState<SubsectionKey>('federal');
  const stateTax = useAdminStateTax(employee.id);
  const taxSetup = useAdminTaxSetup(employee.id);

  return (
    <div className="employee-setup-step profile-step-body">
      <div className="profile-side">
        {statusPill}
        <nav className="profile-subnav" aria-label="Taxes & Exemptions subsections">
          {SUBSECTIONS.map((s) => (
            <button
              key={s.key}
              type="button"
              className={s.key === sub ? 'profile-subnav-item--active' : ''}
              onClick={() => setSub(s.key)}
            >
              {s.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="profile-step-content">
        {sub === 'jurisdiction' && (
          <p className="muted">
            Read-only. The work and residence states come from EPN&apos;s own records; local jurisdictions need the tax engine integration (EPN-22), which is not
            connected yet. Nothing is calculated, inferred or saved here.
          </p>
        )}

        {sub === 'federal' && <FederalTaxElectionSection employeeId={employee.id} />}

        {sub === 'state' && <StateElectionsSection employee={employee} stateTax={stateTax} />}

        {sub === 'state' && (
          <StateIncomeTaxSection employeeId={employee.id} stateTax={stateTax} onOpenReciprocityReview={() => setSub('settings')} />
        )}

        {sub === 'settings' && <AdminTaxSettingsPanel employeeId={employee.id} stateTax={stateTax} taxSetup={taxSetup} />}

        {sub === 'settings' && <ReciprocityReviewSection employeeId={employee.id} onChanged={taxSetup.reload} />}

        {sub === 'jurisdiction' && <JurisdictionSummary stateTax={stateTax} />}

        {footer}
      </div>
    </div>
  );
}
